import { formatInr } from "@/utils/basicFunctions.util";
import { useState } from "react";
import type {
  DiscountCalculatorData,
  DiscountType,
} from "../discountCalculator.type";

// Number inputs are kept as typed strings so a field can be cleared; an
// empty or invalid field counts as 0 (like the preview's parseFloat || 0).
const toNumber = (value: string) => Math.max(0, parseFloat(value) || 0);

// The preview's `[data-dc]` calculator: bill, Rupees off / Percent off,
// discount value and an optional minimum bill. Everything shown is derived
// from those four inputs during render.
export function useDiscountCalculator(data: DiscountCalculatorData) {
  const firstType = data.types[0];
  const initialState = {
    bill: String(data.initialBill),
    type: firstType.key as DiscountType,
    value: firstType.range.initial,
    min: String(data.initialMin),
  };
  const [state, setState] = useState(initialState);

  const setBill = (bill: string) => setState((state) => ({ ...state, bill }));
  const setMin = (min: string) => setState((state) => ({ ...state, min }));
  const setValue = (value: number) =>
    setState((state) => ({ ...state, value }));
  // Switching type resets the slider to that type's range and start value.
  const setType = (type: DiscountType) => {
    const option = data.types.find((item) => item.key === type) ?? firstType;
    setState((state) => ({ ...state, type, value: option.range.initial }));
  };

  const typeOption =
    data.types.find((item) => item.key === state.type) ?? firstType;
  const bill = toNumber(state.bill);
  const min = toNumber(state.min);
  const isEligible = bill >= min;
  const rawDiscount =
    state.type === "flat"
      ? state.value
      : Math.round((bill * state.value) / 100);
  const discount = isEligible ? Math.min(rawDiscount, bill) : 0;
  const final = bill - discount;

  const valueText =
    state.type === "flat" ? formatInr(state.value) : `${state.value}%`;
  const payWidth = bill > 0 ? (final / bill) * 100 : 100;
  const saveWidth = bill > 0 ? (discount / bill) * 100 : 0;
  const sayText = !isEligible
    ? data.say.belowMin.replace("{min}", formatInr(min))
    : discount > 0
      ? data.say.saves
          .replace("{amount}", formatInr(discount))
          .replace(
            "{percent}",
            bill > 0 ? ((discount / bill) * 100).toFixed(1) : "0",
          )
      : data.say.none;

  return {
    state,
    setBill,
    setMin,
    setValue,
    setType,
    range: typeOption.range,
    valueText,
    billText: formatInr(bill),
    discountText: formatInr(discount),
    finalText: formatInr(final),
    payWidth,
    saveWidth,
    sayText,
  };
}
